import { DateTime } from 'luxon';
import { InvalidArgumentError } from '../errors/InvalidArgumentError';
import { isNullish } from '../null-utils';

/** A mock value for use in storybook. */
export const MOCK_ARRIVAL_DATE = 'Monday, 4/11';

/**
 * Validate whether the JS Date object is considered valid.
 * @param date - The date object to validate.
 * @returns `true` if the date is valid.
 */
export const isValidDate = (date: Date): boolean => {
  // Logic borrowed from https://stackoverflow.com/a/1353711
  return date instanceof Date && !Number.isNaN(date.valueOf());
};

/**
 * Accepts a date in a known string format and a string specifying the format and
 * returns a `Date` object.
 * @param dateString - A date string in a known format.
 * @param formatString - A string which denotes the format.
 * @returns A date object respective to the input date strings.
 * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 * @throws If the date string cannot be converted by the format string.
 * @example ```tsx
 * const date = dateFromFormattedDateString(
 *  '26-07-2022 00:00 America/Los_Angeles',
 *  'dd-LL-yyyy HH:mm z'
 * );
 * date.toUTCString(); // "Tue, 26 Jul 2022 07:00:00 GMT"
 * ```
 */
export const dateFromFormattedDateString = (
  dateString: string,
  formatString: string
): Date => {
  const errorMessage = `The date "${dateString}" for format string "${formatString}" is not valid.`;
  let date: Date;

  try {
    date = DateTime.fromFormat(dateString, formatString).toJSDate();
  } catch (e) {
    throw new InvalidArgumentError(errorMessage, { cause: e as Error });
  }

  if (!isValidDate(date)) {
    throw new InvalidArgumentError(errorMessage);
  }

  return date;
};

/**
 * Takes a date and coerces it to a specific timezone. Will NOT change
 * the time, only the timezone and return a valid date in that timezone.
 *
 * **Why can't we just use `DateTime.fromJSDate(date).setZone(timezone)`?**
 * The `setZone` method will change the time to match the timezone. This method
 * will only change the timezone and keep the time the same.
 *
 * This is useful in scenarios when handling user input around specified dates where
 * the timezone is also a configurable option. Calling new Date() automatically
 * sets the timezone to the local timezone of the user's machine.
 *
 * @example
 * ```tsx
 * const date = new Date('2022-07-26T00:00:00Z');
 * const timezone = 'America/Los_Angeles';
 * const coercedDate = coerceDateToZone(date, timezone);
 * coercedDate.toUTCString(); // "Tue, 26 Jul 2022 07:00:00 GMT"
 * ```
 * @param date - The date to coerce.
 * @param timezone - The timezone to coerce the date to.
 * @returns A new date object with the same time but in the specified timezone.
 */
export function coerceDateToZone(date: Date, timezone: string): DateTime {
  /**
   * Extract the date parts and create a new DateTime object.
   */
  const coercedDate = DateTime.fromObject(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1, // There is a difference between JS and Luxon months
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes()
    },
    { zone: timezone }
  );

  return coercedDate;
}

/**
 * Creates a DateTime from an RFC 2822 string.
 * @param text - The RFC 2822 string.
 * @returns The DateTime object.
 */
export function fromRFC2822(text: string): DateTime {
  return DateTime.fromRFC2822(text);
}

/**
 * Converts a timestamp to a local datetime string in the formatter's timezone.
 * @param timestamp - The timestamp to convert.
 * @param formatter - The `Intl.DateTimeFormat` to use for formatting.
 * @returns A string in the format "YYYY-MM-DDTHH:MM" representing the local datetime
 * in the formatter's timezone.
 */
export function convertTimestampToDateTimeLocalString(
  timestamp: number,
  formatter: Intl.DateTimeFormat
): string {
  const parts = formatter.formatToParts(timestamp);
  const lookup = Object.fromEntries(parts.map((p) => [p.type, p.value])) as {
    [key in Intl.DateTimeFormatPartTypes]: string;
  };

  // Construct `YYYY-MM-DDTHH:MM`
  return `${lookup.year}-${lookup.month}-${lookup.day}T${lookup.hour}:${lookup.minute}`;
}

/**
 * Checks if a given date falls on a weekend (Saturday or Sunday).
 *
 * @param date - The date to check.
 * @returns `true` if the date is a weekend, otherwise `false`.
 */
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

/**
 * Represents the interval between two dates in days, hours, and minutes.
 *
 * The interval is **incremental** by hierarchy, meaning that:
 * - The `days` property represents the total number of full days in the interval.
 * - The `hours` property represents the remaining hours after accounting for full days.
 * - The `minutes` property represents the remaining minutes after accounting for full hours.
 */
interface IIntervalBetweenDates {
  /**
   * The total number of full days in the interval.
   */
  days: number;

  /**
   * The remaining hours after accounting for full days.
   */
  hours: number;

  /**
   * The remaining minutes after accounting for full hours.
   */
  minutes: number;

  /**
   * The remaining seconds after accounting for full minutes.
   */
  seconds: number;
}

/**
 * Calculates the number of days between two dates.
 * Uses Luxon for robust date handling across timezones and edge cases.
 *
 * @example
 * ```tsx
 * const startDate = new Date('2023-01-01');
 * const endDate = new Date('2023-01-05');
 * const interval = daysInInterval(startDate, endDate);
 * console.log(interval); // { days: 4, hours: 0, minutes: 0 }
 * ```
 *
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @returns The number of days between startDate and endDate (inclusive).
 * @throws {InvalidArgumentError} If endDate is before startDate.
 */
export const calculateInterval = (
  startDate: Date,
  endDate: Date
): IIntervalBetweenDates => {
  const start = DateTime.fromJSDate(startDate);
  const end = DateTime.fromJSDate(endDate);

  if (end < start) {
    throw new InvalidArgumentError(
      'The endDate must be greater than or equal to the startDate.'
    );
  }

  const { days, hours, minutes, seconds } = end
    .diff(start, ['days', 'hours', 'minutes', 'seconds'])
    .toObject();

  return {
    days: days ?? 0,
    hours: hours ?? 0,
    minutes: minutes ?? 0,
    seconds: seconds ?? 0
  };
};

/**
 * Validates if a given string is in the HH:mm:ss? Format.
 *
 * Seconds are usually optional, so both HH:mm and HH:mm:ss are valid.
 * If seconds are present, they must be in the range 00-59.
 *
 * @example
 * ```tsx
 * isTimeStringValid('14:30:00'); // true
 * isTimeStringValid('2:30:00'); // false
 * isTimeStringValid('14:60:00'); // false
 * isTimeStringValid('14:30'); // true
 * isTimeStringValid('invalid'); // false
 * ```
 * @param time - The time string to validate.
 * @returns `true` if the string is a valid time in HH:mm:ss format, otherwise `false`.
 */
export const isTimeStringValid = (time: string): boolean => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
  return timeRegex.test(time);
};

/**
 * Receives a date and a cutoff time (in HH:mm:ss format) and calculates the
 * remaining time until that cutoff within the same date.
 *
 * If the cutoff time has already passed for that date, the function will
 * return zeros for all fields.
 *
 * @example
 * ```tsx
 * const date = new Date('2023-10-01T10:30:00');
 * const cutoffTime = '15:00:00';
 * const remaining = calculateCutoffWithinADate(date, cutoffTime);
 * console.log(remaining); // { hours: 4, minutes: 30 }
 * const passedDate = new Date('2023-10-01T16:00:00');
 * const passed = calculateCutoffWithinADate(passedDate, cutoffTime);
 * console.log(passed); // { hours: 0, minutes: 0 } -- cutoff has passed
 * ```
 *
 * @param date - The date to calculate from.
 * @param cutoffTime - The cutoff time in HH:mm:ss format.
 * @returns An object containing the remaining hours and minutes until the
 * cutoff time on the given date.
 * @throws {InvalidArgumentError} If the cutoffTime format is invalid.
 */
export const calculateCutoff = (
  date: Date,
  cutoffTime: string
): Omit<IIntervalBetweenDates, 'days'> => {
  if (!isTimeStringValid(cutoffTime)) {
    throw new InvalidArgumentError(
      `The cutoff time "${cutoffTime}" is not valid. It must be in HH:mm:ss? format.`
    );
  }
  // First we parse the cutoff time to extract hours, minutes, and seconds.
  // We assume the format is always HH:mm:ss.
  //
  // If the format is invalid, this will throw a runtime error, which is
  // acceptable since this is a developer error.
  const [cutoffHours, cutoffMinutes, cutoffSeconds] = cutoffTime
    .split(':')
    .map((part) => parseInt(part, 10));

  // Now we get the current time as a DateTime object.
  //
  // We use Luxon to handle edge cases around DST and timezones.
  const now = DateTime.fromObject({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds()
  });

  // Now we create a DateTime object for the cutoff time on the given date.
  //
  // We use Luxon to handle edge cases around DST and timezones.
  const cutoffDateTime = now.set({
    hour: cutoffHours,
    minute: cutoffMinutes,
    // If seconds are not provided, we default to 0.
    //
    // That happens in case the cutoff time is in HH:mm format.
    second: cutoffSeconds ?? 0,
    millisecond: 0
  });

  // If now is after or equal to the cutoff time, we can early exit with zeros.
  if (now >= cutoffDateTime) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  // Finally, we calculate the difference between the two times leveraging Luxon's
  // capabilities to handle edge cases around DST and timezones.
  //
  // We only care about hours and minutes, since days are not relevant in this
  // context (the cutoff is always within the same date).
  const { hours, minutes, seconds } = cutoffDateTime
    .diff(now, ['hours', 'minutes', 'seconds'])
    .toObject();

  return { hours: hours ?? 0, minutes: minutes ?? 0, seconds: seconds ?? 0 };
};

/**
 * Changes the time of a given date to the specified time in HH:mm:ss format.
 *
 * @example
 * ```tsx
 * const date = new Date('2023-10-01T10:30:00');
 * const newDate = changeTimeWithinDate(date, '15:00:00');
 * console.log(newDate); // 2023-10-01T15:00:00
 * ```
 *
 * @throws {InvalidArgumentError} If the time format is invalid.
 * @param date - The date to change the time of.
 * @param time - The new time to set in HH:mm:ss format.
 * @returns A new Date object with the updated time.
 */
export const setTime = (date: Date, time: string): Date => {
  if (!isTimeStringValid(time)) {
    throw new InvalidArgumentError(
      `The time "${time}" is not valid. It must be in HH:mm:ss? format.`
    );
  }

  const [hours, minutes, seconds] = time
    .split(':')
    .map((part) => parseInt(part, 10));

  return DateTime.fromObject({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  })
    .set({ hour: hours, minute: minutes, second: seconds ?? 0 })
    .toJSDate();
};

/**
 * Changes the day of a given date to the specified day of the month.
 *
 * @param date - The original date.
 * @param days - The number of days to add.
 * @returns A new Date object with the updated day.
 */
export const addDays = (date: Date, days: number): Date => {
  return DateTime.fromObject({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds()
  })
    .plus({ days })
    .toJSDate();
};

/**
 * Returns a new Date object representing the start of the day (00:00:00) for the given date.
 *
 * @example
 * ```tsx
 * const date = new Date('2023-10-01T15:30:45');
 * const start = startOfDay(date);
 * console.log(start); // 2023-10-01T00:00:00
 * ```
 * @param date - The date to get the start of the day for.
 * @returns A new Date object set to the start of the day (00:00:00) of the given date.
 */
export const startOfDay = (date: Date): Date => {
  return DateTime.fromObject({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds()
  })
    .startOf('day')
    .toJSDate();
};

/**
 * Utility to check if a given date is tomorrow's date.
 * @param date - Date to check.
 * @returns True if difference in days is 1, false otherwise.
 */
export const isTomorrow = (date: Date): boolean => {
  const today = DateTime.local().startOf('day');
  const targetDate = DateTime.fromJSDate(date).startOf('day');
  const differenceInDays = targetDate.diff(today, 'days').days;
  return differenceInDays === 1;
};
/**
 * Checks if a date is within a specified interval.
 *
 * The `interval` is inclusive of the start and end dates. We can have three scenarios:
 * 1. Both `startDate` and `endDate` are provided: The date must be between or equal to both.
 * 2. Only `startDate` is provided: The date must be on or after the `startDate`.
 * 3. Only `endDate` is provided: The date must be on or before the `endDate`.
 *
 * @param date - The date to check.
 * @param startDate - The start date of the interval.
 * @param endDate - The end date of the interval.
 * @returns True if the date is within the interval, false otherwise.
 * @throws {InvalidArgumentError} If the provided dates are invalid or if both `startDate` and `endDate` are nullish.
 */
export const isWithinInterval = (
  date: Date,
  startDate?: Date | null,
  endDate?: Date | null
): boolean => {
  // First we validate the input dates to early fail in case of issues.
  if (!isValidDate(date)) {
    throw new InvalidArgumentError('The provided date is not valid.');
  }

  if (isNullish(startDate) && isNullish(endDate)) {
    throw new InvalidArgumentError(
      'At least one of startDate or endDate must be provided.'
    );
  }

  if (!isNullish(startDate) && !isValidDate(startDate)) {
    throw new InvalidArgumentError('The provided startDate is not valid.');
  }

  if (!isNullish(endDate) && !isValidDate(endDate)) {
    throw new InvalidArgumentError('The provided endDate is not valid.');
  }

  // Now we leverage Luxon to perform the interval check with operators
  const dt = DateTime.fromJSDate(date);
  const startDt = startDate ? DateTime.fromJSDate(startDate) : null;
  const endDt = endDate ? DateTime.fromJSDate(endDate) : null;

  // If both dates are provided, we need to check the full interval
  if (!isNullish(startDt) && !isNullish(endDt)) {
    return dt >= startDt && dt <= endDt;
  }

  // Now we check the single-sided intervals, since we know one side is nullish
  // If only start date is provided, we check if date is after or equal to start date
  if (!isNullish(startDt)) {
    return dt >= startDt;
  }

  // If we reach here, only end date is provided, we check if date is before or equal to end date
  // It's safe to assert endDt is not nullish here.
  return dt <= endDt!;
};

/**
 * Receives a date and extracts the date string in 'YYYY-MM-DD' format.
 *
 * @example
 * ```tsx
 * const date = new Date('2023-10-01T15:30:45Z');
 * const dateString = extractDateStringFromDate(date);
 * console.log(dateString); // '2023-10-01'
 * ```
 *
 * @param date - The date to extract the string from.
 * @returns The extracted date string in 'YYYY-MM-DD' format.
 * @throws {InvalidArgumentError} If the provided date is invalid.
 */
export const extractDateStringFromDate = (date: Date): string => {
  if (!isValidDate(date)) {
    throw new InvalidArgumentError(
      `The date is not valid and cannot extract date string.`
    );
  }

  return date.toISOString().split('T')[0];
};
